import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowRightLeft, Check, X, Loader2 } from 'lucide-react'
import {
  listIncomingTransfers,
  acceptTransfer,
  declineTransfer,
  type IncomingTransfer,
} from '../lib/api'

/**
 * Phase 98 TRANSFER-06 — banner on My Apps listing pending incoming
 * ownership transfers addressed to the current user.
 *
 * - Renders one row per incoming transfer with Accept + Decline buttons.
 * - When the URL has ?incoming-transfer={id}, the matching row gets a
 *   highlight ring (deep-link from a bell notification).
 * - When `incoming.length === 0` after the initial fetch, returns null
 *   (no chrome).
 * - On Accept: calls api.acceptTransfer, removes the row, optionally
 *   invokes `onAfterAccept` so MyApps can refetch its app lists.
 * - On Decline: calls api.declineTransfer, removes the row.
 *
 * Per CLAUDE.md: Tailwind only, no setInterval (user-action refresh only),
 * lucide-react v0.321 icons only (ArrowRightLeft / Check / X / Loader2 all
 * confirmed present in v0.321).
 */
export function IncomingTransfersBanner({
  onAfterAccept,
}: {
  onAfterAccept?: () => void
}) {
  const [incoming, setIncoming] = useState<IncomingTransfer[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  const highlightParam = searchParams.get('incoming-transfer')
  const highlightId = highlightParam ? Number(highlightParam) : null

  // L-2: wrap reload in useCallback so the exhaustive-deps lint rule is happy.
  const reload = useCallback(async () => {
    try {
      const res = await listIncomingTransfers()
      setIncoming(res.incoming)
    } catch {
      // Non-fatal — show no banner rather than an error chrome at the top.
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  function clearHighlightIfMatches(transferId: number) {
    if (highlightId === transferId) {
      const next = new URLSearchParams(searchParams)
      next.delete('incoming-transfer')
      setSearchParams(next, { replace: true })
    }
  }

  async function handleAccept(transferId: number, appDisplay: string) {
    setBusyId(transferId)
    setError(null)
    setStatus(null)
    try {
      await acceptTransfer(transferId)
      setStatus(`Transfer accepted. You now own '${appDisplay}'.`)
      setIncoming((cur) => cur.filter((t) => t.transferId !== transferId))
      clearHighlightIfMatches(transferId)
      onAfterAccept?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Accept failed')
    } finally {
      setBusyId(null)
    }
  }

  async function handleDecline(transferId: number) {
    setBusyId(transferId)
    setError(null)
    setStatus(null)
    try {
      await declineTransfer(transferId)
      setStatus('Transfer declined.')
      setIncoming((cur) => cur.filter((t) => t.transferId !== transferId))
      clearHighlightIfMatches(transferId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Decline failed')
    } finally {
      setBusyId(null)
    }
  }

  if (incoming.length === 0) return null

  return (
    <div
      data-testid="incoming-transfers-banner"
      className="mb-6 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4"
    >
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold">
        <ArrowRightLeft className="w-4 h-4" />
        Pending ownership transfers
      </div>
      <ul className="space-y-2">
        {incoming.map((t) => {
          const isHighlighted = highlightId === t.transferId
          const appLabel = t.appDisplayName || t.appName
          return (
            <li
              key={t.transferId}
              data-testid={`incoming-transfer-${t.transferId}`}
              data-highlighted={isHighlighted ? 'true' : 'false'}
              className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-card border ${
                isHighlighted
                  ? 'border-primary ring-2 ring-primary/40'
                  : 'border-border'
              }`}
            >
              <div className="text-sm min-w-0 flex-1">
                <strong>{t.fromEmail || 'Someone'}</strong> wants to transfer
                ownership of <strong>{appLabel}</strong> to you.
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => handleAccept(t.transferId, appLabel)}
                  disabled={busyId === t.transferId}
                  className="px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-sm inline-flex items-center gap-1"
                >
                  {busyId === t.transferId ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Accept
                </button>
                <button
                  onClick={() => handleDecline(t.transferId)}
                  disabled={busyId === t.transferId}
                  className="px-3 py-1.5 rounded-lg border border-border hover:bg-secondary disabled:opacity-50 text-sm inline-flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Decline
                </button>
              </div>
            </li>
          )
        })}
      </ul>
      {error && (
        <div className="mt-3 p-2 rounded-lg bg-destructive/10 text-destructive text-xs">
          {error}
        </div>
      )}
      {status && (
        <div className="mt-3 p-2 rounded-lg bg-green-500/10 text-green-700 dark:text-green-400 text-xs">
          {status}
        </div>
      )}
    </div>
  )
}
